import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MovaraMark } from "@/components/Brand";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("dana@movoraboard.com");
  const [password, setPassword] = useState("Movora2026!");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/app/today");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-app-bg flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-sidebar text-white flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-2.5 text-white">
          <MovaraMark className="h-7 w-8" />
          <span className="font-heading font-semibold text-lg">MovaRaBoard</span>
        </Link>
        <div className="max-w-md">
          <p className="op-label text-white/50 mb-3">Operations control</p>
          <h2 className="font-heading text-3xl font-medium leading-tight mb-4">
            See what's moving, what's stuck, and what needs action next.
          </h2>
          <p className="text-white/60 text-[15px] leading-relaxed">
            One clear board for shipment jobs, delays, missing documents, customer updates,
            and team handoffs.
          </p>
        </div>
        <div className="text-white/40 text-xs font-mono-data">
          © 2026 MovaRaBoard · Shipment Visibility Platform
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8 text-brand">
            <MovaraMark className="h-7 w-8" />
            <span className="font-heading font-semibold text-lg text-ink">MovaRaBoard</span>
          </div>
          <h1 className="font-heading text-2xl font-black text-[#0B132B] mb-1">Sign in</h1>
          <p className="text-sm text-ink-2 mb-7">Access your operations dashboard.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="op-label block mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                enterKeyHint="next"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-10 px-3 rounded-sm border border-line bg-surface text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                required
              />
            </div>
            <div>
              <label className="op-label block mb-1.5">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                enterKeyHint="go"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-10 px-3 rounded-sm border border-line bg-surface text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-[13px] text-danger bg-[#E9EEF3] border border-[#C9D6E2] rounded-sm px-3 py-2">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full h-10 rounded-sm bg-[#D97706] hover:bg-[#B45309] text-white text-sm font-bold flex items-center justify-center gap-2 border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
              {!busy && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="mt-6 p-3 rounded-sm bg-white border-2 border-[#0B132B]">
            <p className="text-[13px] text-ink-2">Want to explore first?{" "}
              <Link to="/sample-board" data-testid="login-sample-link" className="text-brand font-medium hover:underline">View the Sample Board</Link>
            </p>
          </div>
          <Link to="/" className="block mt-5 text-[13px] text-steel hover:text-brand transition-colors">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
